import { queryOptions, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DeptTable } from '~/components/features/admin/dept-table';
import { PageContainer } from '~/components/layouts/page-container';
import { Button } from '~/components/ui/button';
import { useDocumentTitle } from '~/hooks/use-document-title';
import { api } from '~/lib/api';

const orgsQueryOptions = queryOptions({
  queryKey: ['admin', 'organizations'] as const,
  queryFn: async () => {
    const res = await api.api.admin.organizations.$get();
    if (!res.ok) throw new Error('Failed to fetch organizations');
    return res.json();
  }
});

function deptsQueryOptions(orgId: string) {
  return queryOptions({
    queryKey: ['admin', 'departments', orgId] as const,
    queryFn: async () => {
      const res = await api.api.admin.organizations[':orgId'].departments.$get({
        param: { orgId }
      });
      if (!res.ok) throw new Error('Failed to fetch departments');
      return res.json();
    }
  });
}

export const Route = createFileRoute('/_authenticated/admin/organizations/$orgId/departments')({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(deptsQueryOptions(params.orgId)),
  component: DepartmentsPage
});

function DepartmentsPage(): React.ReactNode {
  const { t } = useTranslation();
  const { orgId } = Route.useParams();
  const { data } = useQuery(deptsQueryOptions(orgId));
  const departments = data?.departments ?? [];
  const { data: orgsData } = useQuery(orgsQueryOptions);
  const orgName = orgsData?.organizations.find((o) => o.id === orgId)?.name;
  useDocumentTitle(orgName ?? t('admin.departments.title'));
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <PageContainer
      title={orgName ?? t('admin.departments.title')}
      breadcrumbs={[
        { label: t('admin.organizations.title'), href: '/admin/organizations' },
        { label: orgName ?? t('admin.departments.title') }
      ]}
      actions={
        <Button
          onClick={() => {
            setCreateOpen(true);
          }}
        >
          <Plus className="size-4" />
          {t('admin.departments.newDept')}
        </Button>
      }
    >
      <DeptTable
        orgId={orgId}
        departments={departments}
        createOpen={createOpen}
        onCreateOpenChange={setCreateOpen}
      />
    </PageContainer>
  );
}
